import React, { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { Button, Drawer, Icon, List, notification, Typography } from 'antd'
import fileDownload from 'js-file-download'
import PropTypes from 'prop-types'

import {
  COMPLETED_DOCUMENT_MENU_SCRAPING_JOBS_FOR_MENU,
  DOCUMENT_MENU_SCRAPING_JOB,
} from '../../menus/graphql/queries'
import { formatDate } from '../services/formatter'

import LondonDateTime from './london-date-time'
import Spinner from './spinner'

const HistoryItem = ({ completedDate, scrapingStatus }) => (
  <Typography.Text>
    At <LondonDateTime date={completedDate} /> the scraping engine returned an
    event with
    <Typography.Text strong> {scrapingStatus}</Typography.Text>
    {' status'}
  </Typography.Text>
)

HistoryItem.propTypes = {
  completedDate: PropTypes.string.isRequired,
  scrapingStatus: PropTypes.string.isRequired,
}

const DocumentMenuScrapingHistoryDrawer = ({
  menuId,
  buttonName,
  buttonSize,
}) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [documentMenuScrapingJobId, setDocumentMenuScrapingJobId] = useState()

  const {
    data: { completedDocumentMenuScrapingJobsForMenu } = {},
    loading: isLoadingCompletedMenuScrapingJobs,
    refetch: refetchCompletedDocumentMenuScrapingJobs,
  } = useQuery(COMPLETED_DOCUMENT_MENU_SCRAPING_JOBS_FOR_MENU, {
    variables: { menuId },
    fetchPolicy: 'no-cache',
  })

  const {
    data: { documentMenuScrapingJob = {} } = {},
    loading: isLoadingDocumentMenuScrapingJob,
    refetch: refetchDocumentMenuScrapingJob,
  } = useQuery(DOCUMENT_MENU_SCRAPING_JOB, {
    skip: !documentMenuScrapingJobId,
    variables: { id: documentMenuScrapingJobId },
    onCompleted() {
      if (!documentMenuScrapingJobId) return

      fileDownload(
        JSON.stringify(
          documentMenuScrapingJob.documentMenuScrapingResponse.data,
          null,
          2,
        ),
        `${menuId}-${formatDate(documentMenuScrapingJob.completedDate)}.json`,
      )
    },
    onError({ message }) {
      notification.open({
        message: 'Download scraping response failed.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (!documentMenuScrapingJobId) return
    refetchDocumentMenuScrapingJob()
  }, [documentMenuScrapingJobId, refetchDocumentMenuScrapingJob])

  const onButtonClick = useCallback(() => {
    refetchCompletedDocumentMenuScrapingJobs()
    setIsDrawerVisible(true)
  }, [refetchCompletedDocumentMenuScrapingJobs])

  const onClose = useCallback(() => setIsDrawerVisible(false), [])

  return (
    <>
      <Button size={buttonSize} onClick={onButtonClick}>
        {buttonName}
        <Icon type="eye" />
      </Button>
      {isLoadingCompletedMenuScrapingJobs ? (
        <Spinner />
      ) : (
        <Drawer
          title="Scraping response history"
          placement="right"
          onClose={onClose}
          visible={isDrawerVisible}
          width="50%"
        >
          <List
            size="small"
            itemLayout="horizontal"
            dataSource={completedDocumentMenuScrapingJobsForMenu}
            renderItem={completedDocumentMenuScrapingJob => (
              <List.Item>
                <HistoryItem {...completedDocumentMenuScrapingJob} />
                <Button
                  size="small"
                  loading={
                    isLoadingDocumentMenuScrapingJob &&
                    completedDocumentMenuScrapingJob.id ===
                      documentMenuScrapingJobId
                  }
                  onClick={() =>
                    setDocumentMenuScrapingJobId(
                      completedDocumentMenuScrapingJob.id,
                    )
                  }
                >
                  Download
                  <Icon type="download" />
                </Button>
              </List.Item>
            )}
          />
        </Drawer>
      )}
    </>
  )
}

DocumentMenuScrapingHistoryDrawer.propTypes = {
  menuId: PropTypes.string.isRequired,
  buttonName: PropTypes.string.isRequired,
  buttonSize: PropTypes.string,
}

DocumentMenuScrapingHistoryDrawer.defaultProps = {
  buttonSize: 'small',
}

export default DocumentMenuScrapingHistoryDrawer
